import ImpersonationBanner from '@/buyers/components/ImpersonationBanner'
import FrameShared from '@/gf/components/Frame'
import useConfig from '@/gf/hooks/useConfig'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import UserContext from '@/gf/modules/User'
import UNQUOTED_RFQ_COUNT from '@/gf/queries/quotes'
import * as api from '@/suppliers/modules/api'
import { useQuery } from '@apollo/client'
import React from 'react'
import { FrameC } from '../../types'
import { useFetchConversationsQuery } from '../_gen/gql'
import useGqlClient from '../hooks/useGqlClient'
import useSession from '../hooks/useSession'
import ConversationM from '../modules/Conversation'

const SuppliersFrame: FrameC = (props) => {
  // -1 indicates the notification count is loading
  const [ordersNotificationCount, setOrdersNotificationCount] = React.useState<number>(-1)
  const config = useConfig()
  const session = useSession()
  const { user, store, featureFlags } = session
  const gqlClient = useGqlClient()

  const defaultFilters = [['equals', 'state', 'received']]
  const filters = UserContext.isAdmin(user)
    ? // Admin
      FormatFilters.andFilters(defaultFilters)
    : // Not Admin
      store?.onboardedSuppDashDatetime
      ? FormatFilters.andFilters([
          ['equals', 'storeId', store.id],
          ['greater_than', 'inserted_at', store.onboardedSuppDashDatetime],
          ...defaultFilters,
        ])
      : FormatFilters.andFilters([['equals', 'storeId', store?.id], ...defaultFilters])

  const defaultRfqFilters = []
  const rfqFilters = UserContext.isAdmin(user)
    ? FormatFilters.andFilters(defaultRfqFilters)
    : FormatFilters.andFilters([['equals', 'storeId', store?.id], ...defaultRfqFilters])

  React.useEffect(() => {
    api
      .searchStoreOrdersLite({ filters })
      .then(({ data }) =>
        setOrdersNotificationCount(data.storeOrdersSearchLite.pagination.totalResults)
      )
  }, [])

  const { data, loading } = useQuery(UNQUOTED_RFQ_COUNT, {
    variables: {
      filters: rfqFilters,
    },
    fetchPolicy: 'no-cache',
  })

  // -1 means the count is loading
  const rfqsNotificationCount = loading || !data ? -1 : data.unquotedRfqsCount.count

  // Inbox unread messages notification
  const hasUnreadMessages = useFetchConversationsQuery({
    client: gqlClient,
    variables: {
      filters: FormatFilters.andFilters(
        FilterSet.toApiFilters([ConversationM.unreadMessagesConversationFilter()])
      ),
      page: 1,
      pageSize: 1,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.CONVERSATION_POLL_INTERVAL,
  }).data?.fetchConversations.conversations.some((c) => c.unreadMessages)

  const navigation = React.useMemo(() => {
    const result = [
      { name: 'Products', href: '/products' },
      { name: 'Orders', href: '/orders', notificationCount: ordersNotificationCount },
      { name: 'RFQs', href: '/rfqs', notificationCount: rfqsNotificationCount },
      { name: 'Customers', href: '/customers' },
      { name: 'Transactions', href: '/transactions' },
      { name: 'Reports', href: '/reports' },
    ]

    if (UserContext.isAdmin(user)) result.push({ name: 'Metrics', href: '/metrics' })

    return result
  }, [ordersNotificationCount, rfqsNotificationCount, featureFlags])

  const userNavigation = [
    ...(store?.showPartsHub ? [{ name: 'Parts Hub', href: config.buyersUrl }] : []),
    ...(store?.dealerHubEnabled ? [{ name: 'Sales Hub', href: config.dealersUrl }] : []),
    { name: 'Settings', href: '/settings' },
    { name: 'Back to Gearflow.com', href: config.marketingUrl },
    { name: 'Logout', href: `${config.loginBaseUrl}/logout` },
  ]

  return (
    <FrameShared
      navigation={navigation}
      userNavigation={userNavigation}
      user={user}
      // Notification page isn't implemented for suppliers yet
      showNotificationPageLink={false}
      hasUnreadMessages={hasUnreadMessages}
      app={config.app}
      topBanner={session.originalAdminName ? <ImpersonationBanner session={session} /> : undefined}
      {...props}
    />
  )
}

export default SuppliersFrame
